<script>
import { ref, provide, useTemplateRef } from 'vue';
import { useMessageSelection } from 'dashboard/composables/useMessageSelection.js';
import ForwardMessageModal from 'dashboard/modules/conversations/components/ForwardMessageModal.vue';
import { useElementSize } from '@vueuse/core';
// composable
import { useLabelSuggestions } from 'dashboard/composables/useLabelSuggestions';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';

// components
import ReplyBox from './ReplyBox.vue';
import ConversationPinnedMessage from './ConversationPinnedMessage.vue';
import MessageList from 'next/message/MessageList.vue';
import ConversationLabelSuggestion from './conversation/LabelSuggestion.vue';
import Banner from 'dashboard/components/ui/Banner.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ResizableEditorWrapper from './ResizableEditorWrapper.vue';

// stores and apis
import { mapGetters } from 'vuex';

// mixins
import inboxMixin, { INBOX_FEATURES } from 'shared/mixins/inboxMixin';

// utils
import { emitter } from 'shared/helpers/mitt';
import { calculateScrollTop } from './helpers/scrollTopCalculationHelper';
import { LocalStorage } from 'shared/helpers/localStorage';
import {
  filterDuplicateSourceMessages,
  getReadMessages,
  getUnreadMessages,
} from 'dashboard/helper/conversationHelper';
import {
  viewerOwnsPersonalInboxUnreadState,
  viewerOwnsUnreadState,
} from 'dashboard/helper/unreadOwnership';

// constants
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { REPLY_POLICY } from 'shared/constants/links';
import wootConstants from 'dashboard/constants/globals';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import {
  activeTrainingConversationId,
  isTrainingConversation,
} from 'dashboard/helper/trainingConversation';
import { isAceleresPersonalConversation } from 'dashboard/helper/aceleresConversationState';
import { sortMessagesChronologically } from 'dashboard/helper/messageSorting';
import { galleryReplyOpen } from 'dashboard/composables/aceleresMediaViewerCoordinator';
import { isGroupConversation } from 'dashboard/components-next/message/groupParticipant.js';
import {
  groupDeliveryStuckSince,
  useUnconfirmedClock,
} from 'dashboard/components-next/message/unconfirmedGroupSend.js';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import {
  pinnedMessageIds,
  pinnedMessageSnapshots,
} from 'dashboard/helper/pinnedMessages';

// Aceleres: gestos que provam que a rolagem partiu do usuário. `mousedown`
// entra porque arrastar a barra de rolagem não gera wheel nem touchmove — sem
// ele, quem navega o histórico pela barra não conseguiria voltar ao modo
// "colado no fim". Ver handleScroll.
const USER_SCROLL_INTENT_EVENTS = [
  'wheel',
  'touchmove',
  'keydown',
  'mousedown',
];
// Aceleres: medido no homolog -- o custo de uma pagina de mensagens e quase
// todo FIXO (fila do Puma + auth + meta da conversa). Um pedido de 1 mensagem
// custa praticamente o mesmo que um de 100, entao pagina pequena so multiplica
// a espera: de 20 em 20 sao 5 idas ao servidor para o mesmo trecho que uma so
// traria.
const PREVIOUS_MESSAGES_PAGE_SIZE = 50;
// Distancia do topo em que a proxima pagina ja comeca a ser buscada. Com o
// antigo `scrollTop < 100` o pedido so saia depois que o usuario ENCOSTAVA no
// topo, e ai ele ficava olhando o vazio ate a resposta chegar. Buscando com
// uma tela de sobra, a pagina costuma chegar antes de ele terminar de rolar.
const PREVIOUS_MESSAGES_PREFETCH_OFFSET = 800;

export default {
  components: {
    MessageList,
    ReplyBox,
    ConversationPinnedMessage,
    Banner,
    ConversationLabelSuggestion,
    Spinner,
    ResizableEditorWrapper,
    ForwardMessageModal,
  },
  mixins: [inboxMixin],
  setup() {
    const {
      isSelectionMode,
      selectedMessages,
      selectedCount,
      hasSelection,
      exitSelectionMode,
      enterSelectionMode,
    } = useMessageSelection();

    const conversationPanelRef = ref(null);
    const resizableEditorWrapperRef = ref(null);
    const messagesViewRef = useTemplateRef('messagesViewRef');
    const topBannerRef = useTemplateRef('topBannerRef');
    const { height: containerHeight } = useElementSize(messagesViewRef);
    const { height: topBannerHeight } = useElementSize(topBannerRef);

    const {
      captainTasksEnabled,
      isLabelSuggestionFeatureEnabled,
      getLabelSuggestions,
    } = useLabelSuggestions();

    provide('contextMenuElementTarget', conversationPanelRef);

    return {
      unconfirmedClock: useUnconfirmedClock(),
      captainTasksEnabled,
      getLabelSuggestions,
      isLabelSuggestionFeatureEnabled,
      conversationPanelRef,
      resizableEditorWrapperRef,
      messagesViewRef,
      topBannerRef,
      containerHeight,
      topBannerHeight,
      isSelectionMode,
      selectedMessages,
      selectedCount,
      hasSelection,
      exitSelectionMode,
      enterSelectionMode,
      galleryReplyOpen,
    };
  },
  data() {
    return {
      isLoadingPrevious: true,
      historyHydrationPromises: new Map(),
      isUnmounted: false,
      conversationPanel: null,
      hasUserScrolled: false,
      // Aceleres: distancia ate o fim medida no ULTIMO evento de scroll. E um
      // FATO de onde o painel estava, e nao um sinalizador que alguem pode ter
      // apagado. Ver shouldAutoScrollToBottom.
      lastDistanceFromBottom: 0,
      // A pagina anterior so e buscada quando o painel se move em direcao ao
      // topo. Mensagens novas tambem geram scroll events ao reancorar no fim.
      lastScrollTop: 0,
      // Aceleres: até quando (epoch ms) um scroll ainda conta como vindo de
      // gesto do usuário. Ver markUserScrollIntent / handleScroll.
      userScrollIntentUntil: 0,
      // Aceleres: { id, offset } da mensagem que serve de âncora enquanto a
      // página anterior está a caminho. Ver captureScrollAnchor.
      scrollAnchor: null,
      messageSentSinceOpened: false,
      labelSuggestions: [],
      showForwardModal: false,
      loadingTimedOut: false,
      spinnerTimer: null,
      stickRaf: null,
      stickAfterSendRaf: null,
      showJumpToBottom: false,
      isAttachmentProcessing: false,
      markingReadConversationId: null,
      // Uma transferência pode atualizar o unread_count da conversa aberta.
      // O contador dessa transferência não deve ser consumido como leitura.
      skipUnreadReadOnAssignment: false,
      simulationReplyClock: Date.now(),
      // Relogio das fixadas. Cada fixada tem um prazo escolhido no modal e o
      // banner precisa solta-la na hora — o cron do servidor so passa a cada
      // cinco minutos, e ate la a conversa aberta continuaria mostrando algo
      // que ja venceu. Meio minuto de granularidade basta e nao custa nada:
      // so recalcula a lista de fixadas, nao a de mensagens.
      pinnedClock: Date.now(),
      simulationReplyTimerId: null,
      pinnedClockTimerId: null,
    };
  },

  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      currentUserId: 'getCurrentUserID',
      listLoadingStatus: 'getAllMessagesLoaded',
      currentAccountId: 'getCurrentAccountId',
    }),
    isOpen() {
      return this.currentChat?.status === wootConstants.STATUS_TYPE.OPEN;
    },
    selectionExitLabel() {
      return 'Sair da seleção';
    },
    selectionForwardLabel() {
      return 'Encaminhar mensagens selecionadas';
    },
    selectionLabels() {
      return {
        toolbar: 'Ações das mensagens selecionadas',
      };
    },
    shouldShowLabelSuggestions() {
      return (
        this.isOpen &&
        this.captainTasksEnabled &&
        this.isLabelSuggestionFeatureEnabled &&
        !this.messageSentSinceOpened
      );
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    // Conversas pessoais não exibem o botão de paginação manual. Na abertura,
    // o histórico que a API autoriza para o usuário é carregado em segundo plano.
    isAceleresPersonalConversation() {
      return isAceleresPersonalConversation({
        conversation: this.currentChat,
        inbox: this.inbox,
      });
    },
    // Aceleres: evita o banner de topo nativo quando uma inbox da API Oficial
    // está fora da janela de resposta; a interface não exibe ações extras
    // nesse estado.
    isAceleresOfficialApiExpired() {
      if (this.currentChat?.can_reply !== false) return false;
      const additionalAttributes = this.inbox?.additional_attributes || {};
      const markedOfficial =
        additionalAttributes.aceleres_official_api === true ||
        additionalAttributes.aceleres_official_api === 'true';
      const webhookUrl = this.inbox?.webhook_url || '';
      const usesAceleresWhatsappWebhook =
        webhookUrl.includes('whatsapp.aceleres.ai') &&
        webhookUrl.includes('/chatwoot/webhook/whatsapp_');

      return (
        this.isAWhatsAppChannel ||
        (this.isAPIInbox && (markedOfficial || usesAceleresWhatsappWebhook))
      );
    },
    typingUsersList() {
      const userList = this.$store.getters[
        'conversationTypingStatus/getUserList'
      ](this.currentChat.id);
      return userList;
    },
    isDailyTrainingConversation() {
      return (
        activeTrainingConversationId(this.currentUser) ===
        Number(this.currentChat?.id || 0)
      );
    },
    isTrainingConversationContext() {
      return isTrainingConversation(this.currentChat, this.currentUser);
    },
    isCompletedTrainingConversation() {
      return (
        this.isTrainingConversationContext &&
        this.currentChat?.status === wootConstants.STATUS_TYPE.RESOLVED
      );
    },
    simulationReplyState() {
      if (!this.isDailyTrainingConversation) {
        return null;
      }

      const publicMessages = this.getMessages.filter(message => {
        const messageType = message?.message_type;
        const isActivity = messageType === 2 || messageType === 'activity';
        return !message?.private && !isActivity;
      });
      const lastMessage = publicMessages[publicMessages.length - 1];
      const lastMessageType = lastMessage?.message_type;
      const isOutgoing =
        lastMessageType === 1 || lastMessageType === 'outgoing';
      if (!isOutgoing) {
        return null;
      }

      const rawCreatedAt = lastMessage?.created_at;
      let createdAt = Number(rawCreatedAt);
      if (Number.isFinite(createdAt) && createdAt > 0) {
        createdAt = createdAt > 1000000000000 ? createdAt : createdAt * 1000;
      } else {
        createdAt = Date.parse(rawCreatedAt);
      }

      if (!Number.isFinite(createdAt)) {
        return 'timed_out';
      }

      // Três estados: digitando (0-30s), continua digitando (30-90s) e
      // sem resposta (>90s). O indicador não bloqueia o atendimento.
      const elapsed = this.simulationReplyClock - createdAt;
      if (elapsed <= 30000) {
        return 'pending';
      }
      if (elapsed <= 90000) {
        return 'delayed';
      }
      return 'timed_out';
    },
    isSimulationReplyPending() {
      return (
        this.simulationReplyState === 'pending' ||
        this.simulationReplyState === 'delayed'
      );
    },
    isSimulationReplyTimedOut() {
      return this.simulationReplyState === 'timed_out';
    },
    // Pedido 02/09/2026: "digitando" deixou de ser texto -- no cabeçalho e na
    // pílula sobre o compositor -- e passou a ser UMA bolha de três pontos no
    // fim da conversa, como no WhatsApp. Esta é a única condição que a acende.
    //
    // Só conta quem está DO OUTRO LADO. A bolha nasce à esquerda, no lugar de
    // uma mensagem recebida, então acendê-la porque outro atendente digita
    // diria ao vendedor que o cliente está a escrever quando não está. O
    // typing de agente (`type === 'user'`) deixou de ter indicador; era o que
    // a pílula mostrava e saiu com ela.
    //
    // O broadcast não distingue digitar de gravar áudio (é um liga/desliga que
    // o Laravel aciona para os dois), e a bolha também não: quem diz "gravando
    // áudio..." continua a ser o cabeçalho, que manteve esse rótulo.
    isOtherSideTyping() {
      const alguemDoOutroLado = this.typingUsersList.some(
        user => user?.type !== 'user'
      );

      return alguemDoOutroLado || this.isSimulationReplyPending;
    },
    // O aviso do treino simulado NÃO é "digitando" -- é um recado ao vendedor
    // de que o cliente não respondeu -- e por isso continua na pílula
    // flutuante. A condição é a mesma de antes: enquanto alguém digita, quem
    // manda é a bolha.
    showSimulationTimeoutNotice() {
      return (
        this.isSimulationReplyTimedOut && this.typingUsersList.length === 0
      );
    },
    // O texto vive aqui, e nao solto no template, porque a folha de lint deste
    // projeto trata frase escrita direto no HTML como erro. Antes ele era o
    // ramo de um ternario dentro de `{{ }}` -- o outro ramo era o "digitando",
    // que saiu.
    simulationTimeoutMessage() {
      return 'O cliente não respondeu, você pode enviar uma mensagem se achar necessário.';
    },
    // Aceleres 2026-09-14: faixa quando os envios para o GRUPO ficam sem
    // recibo do WhatsApp (ver unconfirmedGroupSend.js). Em 10-14/09/2026 a
    // conexao Facundo passou quatro dias assim com as bolhas dizendo "enviada";
    // reconectar a conta foi o que devolveu a entrega.
    groupDeliveryStuckTime() {
      if (!isGroupConversation(this.currentChat)) return null;
      const since = groupDeliveryStuckSince(
        this.currentChat.messages,
        this.unconfirmedClock
      );
      return since ? messageTimestamp(since, 'HH:mm') : null;
    },
    getMessages() {
      const messages = this.currentChat.messages || [];
      if (this.isAWhatsAppChannel) {
        return sortMessagesChronologically(
          filterDuplicateSourceMessages(messages)
        );
      }
      return sortMessagesChronologically(messages);
    },
    // Aceleres: assinatura que muda ao trocar de conversa ou quando a ultima
    // mensagem muda. Nao inclui a quantidade: carregar historico no inicio da
    // lista nao pode ser confundido com uma mensagem nova e reancorar no fim.
    lastMessageSignature() {
      const messages = this.getMessages;
      const last = messages[messages.length - 1];
      return `${this.currentChat?.id || 'none'}:${last?.id || 'empty'}`;
    },
    shouldDisplayJumpToBottom() {
      return this.showJumpToBottom && !this.isAttachmentProcessing;
    },
    readMessages() {
      return getReadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    unReadMessages() {
      return getUnreadMessages(
        this.getMessages,
        this.currentChat.agent_last_seen_at
      );
    },
    shouldShowSpinner() {
      const hasVisibleMessages = this.getMessages.length > 0;

      return (
        (!hasVisibleMessages &&
          this.currentChat &&
          this.currentChat.dataFetched === undefined) ||
        (!hasVisibleMessages &&
          !this.listLoadingStatus &&
          this.isLoadingPrevious)
      );
    },
    // Check there is a instagram inbox exists with the same instagram_id
    hasDuplicateInstagramInbox() {
      const instagramId = this.inbox.instagram_id;
      const { additional_attributes: additionalAttributes = {} } = this.inbox;
      const instagramInbox =
        this.$store.getters['inboxes/getInstagramInboxByInstagramId'](
          instagramId
        );

      return (
        this.inbox.channel_type === INBOX_TYPES.FB &&
        additionalAttributes.type === 'instagram_direct_message' &&
        instagramInbox
      );
    },

    replyWindowBannerMessage() {
      if (this.isAWhatsAppChannel) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_CAN_REPLY');
      }
      if (this.isAPIInbox) {
        const { additional_attributes: additionalAttributes = {} } = this.inbox;
        if (additionalAttributes) {
          const {
            agent_reply_time_window_message: agentReplyTimeWindowMessage,
            agent_reply_time_window: agentReplyTimeWindow,
          } = additionalAttributes;
          return (
            agentReplyTimeWindowMessage ||
            this.$t('CONVERSATION.API_HOURS_WINDOW', {
              hours: agentReplyTimeWindow,
            })
          );
        }
        return '';
      }
      return this.$t('CONVERSATION.CANNOT_REPLY');
    },
    replyWindowLink() {
      if (this.isAFacebookInbox || this.isAnInstagramChannel) {
        return REPLY_POLICY.FACEBOOK;
      }
      if (this.isAWhatsAppCloudChannel) {
        return REPLY_POLICY.WHATSAPP_CLOUD;
      }
      if (this.isATiktokChannel) {
        return REPLY_POLICY.TIKTOK;
      }
      if (!this.isAPIInbox) {
        return REPLY_POLICY.TWILIO_WHATSAPP;
      }
      return '';
    },
    replyWindowLinkText() {
      if (
        this.isAWhatsAppChannel ||
        this.isAFacebookInbox ||
        this.isAnInstagramChannel
      ) {
        return this.$t('CONVERSATION.24_HOURS_WINDOW');
      }
      if (this.isATiktokChannel) {
        return this.$t('CONVERSATION.48_HOURS_WINDOW');
      }
      if (!this.isAPIInbox) {
        return this.$t('CONVERSATION.TWILIO_WHATSAPP_24_HOURS_WINDOW');
      }
      return '';
    },
    unreadMessageCount() {
      // Supervisores e gestores podem visualizar a pendência sem consumir a
      // leitura do responsável. `makeMessagesRead` mantém a autorização de
      // leitura separada da exibição deste contador.
      return this.currentChat.unread_count || 0;
    },
    unreadMessageLabel() {
      const count = this.unreadMessageCount;
      const label =
        this.unreadMessageCount > 1
          ? 'CONVERSATION.UNREAD_MESSAGES'
          : 'CONVERSATION.UNREAD_MESSAGE';
      return `${count} ${this.$t(label)}`;
    },
    viewerOwnsUnreadState() {
      const inboxAttributes = this.inbox?.additional_attributes || {};
      return viewerOwnsUnreadState({
        currentUserId: this.currentUserId,
        assigneeId: this.currentChat?.meta?.assignee?.id,
        personalInboxAttributes: {
          ...inboxAttributes,
          connection_type:
            inboxAttributes.connection_type ||
            this.currentChat?.inbox_connection_type,
          owner_chatwoot_user_id:
            inboxAttributes.owner_chatwoot_user_id ||
            this.currentChat?.inbox_owner_chatwoot_user_id,
        },
        personalConversationOwnerId:
          this.currentChat?.additional_attributes
            ?.personal_owner_chatwoot_user_id,
      });
    },
    viewerOwnsPersonalInboxUnreadState() {
      const inboxAttributes = this.inbox?.additional_attributes || {};
      return viewerOwnsPersonalInboxUnreadState({
        currentUserId: this.currentUserId,
        personalInboxAttributes: {
          ...inboxAttributes,
          connection_type:
            inboxAttributes.connection_type ||
            this.currentChat?.inbox_connection_type,
          owner_chatwoot_user_id:
            inboxAttributes.owner_chatwoot_user_id ||
            this.currentChat?.inbox_owner_chatwoot_user_id,
        },
        personalConversationOwnerId:
          this.currentChat?.additional_attributes
            ?.personal_owner_chatwoot_user_id,
      });
    },
    firstUnreadMessageId() {
      if (!this.viewerOwnsUnreadState) {
        return null;
      }

      // Hide the visual "new messages" marker as soon as the read request is
      // issued. The authoritative unread_count is still updated by the API;
      // if the request fails, markingReadConversationId is cleared and the
      // marker becomes visible again instead of masking a failed read.
      if (this.markingReadConversationId === this.currentChat?.id) {
        return null;
      }

      return this.unReadMessages[0]?.id || null;
    },
    // Aceleres: as mensagens fixadas (ate quatro) para o banner no topo.
    //
    // O instantaneo guardado na conversa e a base — e ele que sobrevive quando
    // a mensagem esta longe demais para ter sido carregada. Quando ela ESTA na
    // tela, a mensagem viva vence: ela tem os anexos completos e o conteudo
    // atual (uma mensagem editada depois de fixada mostraria o texto velho).
    pinnedMessagesData() {
      const snapshots = pinnedMessageSnapshots(
        this.currentChat,
        this.pinnedClock
      );
      if (!snapshots.length) return [];

      const loaded = new Map(
        (this.currentChat?.messages || []).map(message => [
          Number(message.id),
          message,
        ])
      );

      return snapshots.map(snapshot => {
        const live = loaded.get(Number(snapshot.id));
        return live ? { ...snapshot, ...live } : snapshot;
      });
    },
    conversationHistoryMeta() {
      const liveMeta = this.$store.getters[
        'conversationMetadata/getConversationMetadata'
      ](this.currentChat.id);
      return liveMeta?.history || this.currentChat?.meta?.history || {};
    },
    canLoadPreviousMessages() {
      return this.conversationHistoryMeta?.can_load_previous === true;
    },
    shouldShowPreviousMessagesButton() {
      if (!this.currentChat?.id || this.isAceleresPersonalConversation) {
        return false;
      }

      if (this.isLoadingPrevious || this.canLoadPreviousMessages) {
        return true;
      }

      const historyFlag = this.conversationHistoryMeta?.can_load_previous;
      if (historyFlag === false || this.listLoadingStatus) {
        return false;
      }

      return (this.currentChat.messages?.length || 0) > 0;
    },
    inboxSupportsReplyTo() {
      const incoming = this.inboxHasFeature(INBOX_FEATURES.REPLY_TO);
      const outgoing =
        this.inboxHasFeature(INBOX_FEATURES.REPLY_TO_OUTGOING) &&
        !this.is360DialogWhatsAppChannel;

      return { incoming, outgoing };
    },
  },

  watch: {
    currentChat(newChat, oldChat) {
      const newAssigneeId = newChat?.meta?.assignee?.id || null;
      const oldAssigneeId = oldChat?.meta?.assignee?.id || null;
      if (
        newChat?.id === oldChat?.id &&
        Number(newAssigneeId) !== Number(oldAssigneeId)
      ) {
        this.skipUnreadReadOnAssignment = true;
        this.$nextTick(() => {
          this.skipUnreadReadOnAssignment = false;
        });
      }

      if (newChat.id === oldChat.id) {
        if (newChat.dataFetched === true && oldChat.dataFetched !== true) {
          this.$nextTick(() => this.hydrateCurrentConversationHistory());
        }
        return;
      }
      this.handleExitSelection();
      // Aceleres: nova conversa sempre comeca ancorada no fim (descarta o
      // scroll manual da conversa anterior) para abrir nas mensagens recentes.
      this.hasUserScrolled = false;
      this.lastDistanceFromBottom = 0;
      this.lastScrollTop = this.conversationPanel?.scrollTop || 0;
      this.fetchAllAttachmentsFromCurrentChat();
      this.fetchSuggestions();
      this.messageSentSinceOpened = false;
      this.resetReplyEditorHeight();
      clearTimeout(this.spinnerTimer);
      this.spinnerTimer = null;
      this.loadingTimedOut = false;
      this.anchorToBottomOnOpen();
      this.$nextTick(() => {
        this.markCurrentConversationReadIfNeeded();
        this.hydrateCurrentConversationHistory();
      });
    },
    unreadMessageCount(newCount, oldCount) {
      if (this.skipUnreadReadOnAssignment) {
        this.skipUnreadReadOnAssignment = false;
        return;
      }

      if (newCount > 0 && newCount !== oldCount) {
        this.$nextTick(() => this.markCurrentConversationReadIfNeeded());
      }
    },
    // Aceleres: reancora somente ao trocar de conversa ou receber uma nova
    // mensagem no fim. Mensagens antigas inseridas no inicio preservam o scroll.
    lastMessageSignature() {
      this.$nextTick(() => this.scrollToConversationBottomIfNeeded());
    },
    // A bolha de "digitando" entra no fim da lista e cresce o painel como uma
    // mensagem nova cresceria, entao reancora pela MESMA regra -- quem rolou
    // para cima para ler o historico nao e arrastado para baixo.
    isOtherSideTyping() {
      this.$nextTick(() => this.scrollToConversationBottomIfNeeded());
    },
  },

  created() {
    emitter.on(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
    // when a message is sent we set the flag to true this hides the label suggestions,
    // until the chat is changed and the flag is reset in the watch for currentChat
    emitter.on(BUS_EVENTS.MESSAGE_SENT, this.onMessageSent);
  },

  mounted() {
    this.addScrollListener();
    this.fetchAllAttachmentsFromCurrentChat();
    this.fetchSuggestions();
    this.$nextTick(() => {
      this.markCurrentConversationReadIfNeeded();
      this.hydrateCurrentConversationHistory();
    });
    this.simulationReplyTimerId = window.setInterval(() => {
      this.simulationReplyClock = Date.now();
    }, 1000);
    this.pinnedClockTimerId = window.setInterval(() => {
      this.pinnedClock = Date.now();
    }, 30000);
  },

  unmounted() {
    this.isUnmounted = true;
    this.handleExitSelection();
    if (this.simulationReplyTimerId) {
      window.clearInterval(this.simulationReplyTimerId);
      this.simulationReplyTimerId = null;
    }
    if (this.pinnedClockTimerId) {
      window.clearInterval(this.pinnedClockTimerId);
      this.pinnedClockTimerId = null;
    }
    this.removeBusListeners();
    this.removeScrollListener();
  },

  methods: {
    // Aceleres: desafixar pelo banner. O id vem do banner porque ele sabe qual
    // das fixadas esta em exibicao — a acao de pin alterna, entao mandar o id
    // errado fixaria outra coisa.
    async handleUnpin(messageId) {
      const id = messageId ?? pinnedMessageIds(this.currentChat).at(-1);
      if (!id) return;
      await this.$store.dispatch('pinMessage', {
        accountId: this.currentAccountId,
        conversationId: this.currentChat.id,
        messageId: id,
      });
    },
    // Aceleres: ir ate a fixada. Reaproveita o mesmo caminho de qualquer outro
    // salto para uma mensagem (`onScrollToMessage`), que ja centraliza na
    // viewport e da o flash de destaque.
    scrollToPinnedMessage(messageId) {
      this.onScrollToMessage({ messageId });
    },
    async fetchSuggestions() {
      // start empty, this ensures that the label suggestions are not shown
      this.labelSuggestions = [];

      if (this.isLabelSuggestionDismissed()) {
        return;
      }

      // Early exit if conversation already has labels - no need to suggest more
      const existingLabels = this.currentChat?.labels || [];
      if (existingLabels.length > 0) return;

      if (!this.captainTasksEnabled || !this.isLabelSuggestionFeatureEnabled) {
        return;
      }

      this.labelSuggestions = await this.getLabelSuggestions();

      // once the labels are fetched, we need to scroll to bottom
      // but we need to wait for the DOM to be updated
      // so we use the nextTick method
      this.$nextTick(() => {
        // this param is added to route, telling the UI to navigate to the message
        // it is triggered by the SCROLL_TO_MESSAGE method
        // see setActiveChat on ConversationView.vue for more info
        const { messageId } = this.$route.query;

        // only trigger the scroll to bottom if the user has not scrolled
        // and there's no active messageId that is selected in view
        if (!messageId && !this.hasUserScrolled) {
          this.scrollToBottom();
        }
      });
    },
    isLabelSuggestionDismissed() {
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_LABEL_SUGGESTIONS,
        this.currentAccountId,
        this.currentChat.id
      );
    },
    fetchAllAttachmentsFromCurrentChat() {
      this.$store.dispatch('fetchAllAttachments', this.currentChat.id);
    },
    removeBusListeners() {
      emitter.off(BUS_EVENTS.SCROLL_TO_MESSAGE, this.onScrollToMessage);
      emitter.off(BUS_EVENTS.MESSAGE_SENT, this.onMessageSent);
      if (this.stickAfterSendRaf) {
        window.cancelAnimationFrame(this.stickAfterSendRaf);
        this.stickAfterSendRaf = null;
      }
    },
    // Aceleres: ao enviar msg/imagem, força o scroll a ficar colado no fim
    // por ~1.5s. Um simples scrollToBottom não bastava pra imagens: o <img>
    // carrega depois, cresce o painel e a msg recém-enviada saía da viewport.
    // Também zera hasUserScrolled — quem acabou de enviar quer ver o próprio
    // envio, mesmo que tenha rolado pra cima antes. (pedido 2026-07-01)
    onMessageSent() {
      this.messageSentSinceOpened = true;
      this.stickToBottomAfterSend();
    },
    stickToBottomAfterSend() {
      this.hasUserScrolled = false;
      if (this.stickAfterSendRaf) {
        window.cancelAnimationFrame(this.stickAfterSendRaf);
        this.stickAfterSendRaf = null;
      }
      this.$nextTick(() => {
        this.scrollToBottom();
        let lastHeight = -1;
        let lastClientHeight = -1;
        let frames = 0;
        const step = () => {
          const panel = this.ensurePanel();
          if (!panel) {
            frames += 1;
            if (frames < 90) {
              this.stickAfterSendRaf = window.requestAnimationFrame(step);
              return;
            }
            this.stickAfterSendRaf = null;
            this.refreshJumpToBottom();
            return;
          }
          if (this.hasUserScrolled) {
            this.stickAfterSendRaf = null;
            this.refreshJumpToBottom();
            return;
          }
          if (
            panel.scrollHeight !== lastHeight ||
            panel.clientHeight !== lastClientHeight
          ) {
            lastHeight = panel.scrollHeight;
            lastClientHeight = panel.clientHeight;
            panel.scrollTop = panel.scrollHeight;
          }
          frames += 1;
          if (frames < 90) {
            this.stickAfterSendRaf = window.requestAnimationFrame(step);
          } else {
            this.stickAfterSendRaf = null;
            this.refreshJumpToBottom();
          }
        };
        step();
      });
    },
    // Aceleres: este handler atende DOIS pedidos muito diferentes que viajam
    // no mesmo evento, e tratar os dois igual era o que jogava o atendente para
    // a ultima mensagem no meio da leitura:
    //
    //  1) "leve-me ate a mensagem X" -- vem com messageId (clique no reply-to,
    //     na busca, num item do painel de contato).
    //  2) um aviso generico SEM messageId, disparado pela store: em ADD_MESSAGE
    //     (mensagem nova no fim) e em UPDATE_CONVERSATION -- e este ultimo
    //     dispara a CADA evento `conversation.updated` da conversa aberta:
    //     update_last_seen, digitando, etiqueta, atribuicao, atributo
    //     personalizado. Ou seja, ler a conversa ja provoca o evento.
    //
    // No caso (2) `messageId` cai no default '' e o getElementById nunca acha
    // nada, entao o codigo antigo caia direto no `scrollToBottom()` -- sem
    // passar por nenhuma guarda. Era o unico caminho de descida que ignorava
    // completamente o fato de o atendente estar lendo historico.
    //
    // Agora: sem alvo, vale a mesma regra dos outros reancoramentos. E com
    // alvo que nao esta na tela (mensagem antiga ainda nao carregada) nao se
    // faz nada -- descer para o fim nunca foi o que esse pedido queria.
    onScrollToMessage({ messageId = '' } = {}) {
      this.$nextTick(() => {
        const messageElement = messageId
          ? document.getElementById('message' + messageId)
          : null;
        if (messageElement) {
          // Aceleres: block:'center' deixa o destino bem no meio da viewport
          // (em vez de colado no topo, que tava deixando a msg quase fora);
          // e o highlight class faz um flash de fundo pra agente saber pra
          // onde foi (mimic do WhatsApp). Estilo da classe está em
          // aceleres-overrides.css.
          messageElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          messageElement.classList.add('acl-scroll-highlight');
          setTimeout(() => {
            messageElement.classList.remove('acl-scroll-highlight');
          }, 1400);
        } else if (!messageId && this.shouldAutoScrollToBottom()) {
          this.scrollToBottom();
        } else {
          this.refreshJumpToBottom();
        }
      });
      // Aceleres: era `makeMessagesRead()`, que marca lida SEMPRE. Como este
      // handler roda a cada `conversation.updated`, e marcar lida faz o backend
      // tocar a conversa (gerando outro `conversation.updated`), o par se
      // realimentava: uma chamada de API por volta, sem fim, enquanto a
      // conversa estivesse aberta. A variante guardada so age quando ha
      // nao-lidas de verdade, entao a primeira volta zera e o laco morre.
      this.markCurrentConversationReadIfNeeded();
    },
    addScrollListener(attempt = 0) {
      // Aceleres: o painel (.conversation-panel) pode ainda nao existir no
      // primeiro mounted (MessageList renderiza depois). Resolve via ensurePanel
      // e, se nao achar, tenta de novo no proximo frame (ate ~30) antes de
      // anexar listeners + ancorar. Sem isso, conversationPanel ficava null, o
      // scroll quebrava (TypeError scrollHeight) e o botao nunca aparecia.
      this.isLoadingPrevious = false;
      const panel = this.ensurePanel();
      if (!panel) {
        if (attempt < 30) {
          window.requestAnimationFrame(() => this.addScrollListener(attempt + 1));
        }
        return;
      }
      panel.removeEventListener('scroll', this.handleScroll);
      panel.addEventListener('scroll', this.handleScroll);
      this.lastScrollTop = panel.scrollTop;
      panel.removeEventListener('load', this.onMediaLoad, true);
      panel.addEventListener('load', this.onMediaLoad, true);
      // Aceleres: os gestos que provam intenção de rolar. Passivos porque só
      // observam — não cancelam nem atrasam o scroll nativo.
      USER_SCROLL_INTENT_EVENTS.forEach(eventName => {
        panel.removeEventListener(eventName, this.markUserScrollIntent);
        panel.addEventListener(eventName, this.markUserScrollIntent, {
          passive: true,
        });
      });
      this.anchorToBottomOnOpen();
    },
    removeScrollListener() {
      if (this.stickRaf) {
        window.cancelAnimationFrame(this.stickRaf);
        this.stickRaf = null;
      }
      if (!this.conversationPanel) return;
      this.conversationPanel.removeEventListener('scroll', this.handleScroll);
      this.conversationPanel.removeEventListener('load', this.onMediaLoad, true);
      USER_SCROLL_INTENT_EVENTS.forEach(eventName => {
        this.conversationPanel.removeEventListener(
          eventName,
          this.markUserScrollIntent
        );
      });
    },
    // Aceleres: resolve o elemento rolavel (.conversation-panel) de forma
    // resiliente -- cache (se ainda conectado), senao o $el do ref do
    // MessageList, senao querySelector no $el. Evita o null que quebrava
    // scrollToBottom e escondia o botao quando o painel nao estava pronto.
    ensurePanel() {
      if (this.conversationPanel && this.conversationPanel.isConnected) {
        return this.conversationPanel;
      }
      const exposedElement = this.conversationPanelRef?.messageListElement;
      const fromExpose = exposedElement?.value || exposedElement;
      const fromRef = this.conversationPanelRef && this.conversationPanelRef.$el;
      this.conversationPanel =
        (fromExpose && fromExpose.isConnected ? fromExpose : null) ||
        (fromRef && fromRef.classList && fromRef.classList.contains('conversation-panel')
          ? fromRef
          : null) ||
        (this.$el && this.$el.querySelector
          ? this.$el.querySelector('.conversation-panel')
          : null) ||
        null;
      return this.conversationPanel;
    },
    scrollToBottom() {
      const panel = this.ensurePanel();
      if (!panel) return;
      // Aceleres: sempre rola até o fim absoluto (mensagens mais recentes),
      // independente de mensagens não-lidas — comportamento esperado de chat.
      // Antes, com muitas mensagens não-lidas, o painel abria no topo.
      panel.scrollTop = panel.scrollHeight;
      this.lastDistanceFromBottom = 0;
      this.showJumpToBottom = false;
    },
    // Aceleres: visibilidade do botao flutuante "ir para o fim" pela distancia
    // ate o fim -- independe da heuristica de auto-scroll (que pode falhar).
    refreshJumpToBottom() {
      const panel = this.ensurePanel();
      if (!panel) {
        this.showJumpToBottom = false;
        return;
      }
      const d = panel.scrollHeight - panel.scrollTop - panel.clientHeight;
      this.showJumpToBottom = d > 200;
    },
    // Aceleres: clique do botao -> desce de vez e some.
    jumpToBottom() {
      this.hasUserScrolled = false;
      this.scrollToBottom();
    },
    handleAttachmentProcessingChange(isProcessing) {
      this.isAttachmentProcessing = Boolean(isProcessing);
    },
    // Aceleres: ao carregar uma mídia (ex.: imagem), a altura do painel muda e
    // empurra o conteúdo. Se o usuário não rolou manualmente, reancora no fim
    // para manter as mensagens mais recentes visíveis.
    // Aceleres: quem pode puxar o painel para o fim SOZINHO (midia que carrega,
    // mensagem nova que chega) precisa passar por aqui.
    //
    // Antes a decisao era so `!hasUserScrolled`, e era esse o furo do "subo
    // umas mensagens e o chat me joga para baixo": hasUserScrolled e um
    // sinalizador LEMBRADO, e varios eventos que nao sao do usuario o apagam --
    // o painel encolhe sozinho (album de imagens que fecha no tick do relogio),
    // uma pagina antiga entra e reposiciona o scroll, o cabecalho "Ver
    // atendimentos anteriores" troca de altura. Qualquer um desses dispara um
    // scroll com distancia pequena e zera a marca; a proxima imagem que
    // terminasse de carregar caia no scrollToBottom e levava embora quem estava
    // lendo historico.
    //
    // `lastDistanceFromBottom` nao tem esse problema: e onde o painel estava no
    // ultimo scroll REAL. Carregar midia nao gera scroll, entao no instante da
    // decisao esse valor ainda descreve onde o atendente esta de fato.
    shouldAutoScrollToBottom() {
      return !this.hasUserScrolled && this.lastDistanceFromBottom <= 80;
    },
    onMediaLoad() {
      if (this.shouldAutoScrollToBottom() && this.conversationPanel) {
        this.$nextTick(() => this.scrollToBottom());
      } else {
        this.$nextTick(() => this.refreshJumpToBottom());
      }
    },
    // Aceleres: rola pro fim da conversa quando faz sentido -- nao interfere se
    // o usuario rolou pra cima, ha deep-link ou ha mensagens nao-lidas pendentes.
    scrollToConversationBottomIfNeeded() {
      if (!this.ensurePanel()) return;
      const { messageId } = this.$route.query;
      if (messageId || !this.shouldAutoScrollToBottom()) {
        this.$nextTick(() => this.refreshJumpToBottom());
        return;
      }
      this.scrollToBottom();
      // 2o passe no proximo frame: pega reflows tardios (markdown, fontes, cards
      // que so calculam altura depois do paint) sem depender de carga de midia.
      window.requestAnimationFrame(() => {
        if (this.shouldAutoScrollToBottom() && this.conversationPanel) {
          this.scrollToBottom();
        }
        this.refreshJumpToBottom();
      });
    },
    // Aceleres: ao ABRIR/TROCAR de conversa, "gruda" no fim por ~1.5s. Enquanto
    // o conteudo ainda muda de altura (mensagens do fetch async, imagens, reflow
    // de markdown), reancora no fim a cada mudanca de scrollHeight. Para assim
    // que o usuario rola manualmente (hasUserScrolled) ou apos ~90 frames.
    anchorToBottomOnOpen() {
      if (this.stickRaf) {
        window.cancelAnimationFrame(this.stickRaf);
        this.stickRaf = null;
      }
      let lastHeight = -1;
      let frames = 0;
      const step = () => {
        const panel = this.ensurePanel();
        if (this.hasUserScrolled || !panel) {
          this.stickRaf = null;
          this.refreshJumpToBottom();
          return;
        }

        const height = panel.scrollHeight;
        if (height !== lastHeight) {
          lastHeight = height;
          this.scrollToBottom();
        }
        frames += 1;
        if (frames < 90) {
          this.stickRaf = window.requestAnimationFrame(step);
        } else {
          this.stickRaf = null;
          this.refreshJumpToBottom();
        }
      };
      this.stickRaf = window.requestAnimationFrame(step);
    },
    async loadMoreMessages() {
      await this.fetchPreviousMessages(0);
    },
    async hydrateCurrentConversationHistory() {
      const conversationId = Number(this.currentChat?.id || 0);
      if (
        !conversationId ||
        this.currentChat?.dataFetched !== true ||
        this.isUnmounted
      ) {
        return false;
      }

      const existingPromise = this.historyHydrationPromises.get(conversationId);
      if (existingPromise) return existingPromise;

      const historyHydration = (async () => {
        while (
          Number(this.currentChat?.id || 0) === conversationId &&
          this.currentChat?.dataFetched === true &&
          this.currentChat?.allMessagesLoaded !== true &&
          !this.isUnmounted
        ) {
          if (this.conversationHistoryMeta?.can_load_previous === false) break;

          const oldestMessageId = this.currentChat?.messages?.[0]?.id;
          if (!oldestMessageId) break;

          const previousOldestMessageId = Number(oldestMessageId);
          // A próxima página usa o cursor que só chega nesta resposta.
          // eslint-disable-next-line no-await-in-loop
          const loaded = await this.fetchPreviousMessages(0);
          if (!loaded) break;

          if (
            Number(this.currentChat?.id || 0) !== conversationId ||
            this.isUnmounted
          ) {
            break;
          }

          const nextOldestMessageId = Number(
            this.currentChat?.messages?.[0]?.id
          );
          if (
            !nextOldestMessageId ||
            nextOldestMessageId === previousOldestMessageId ||
            this.currentChat?.allMessagesLoaded === true ||
            this.conversationHistoryMeta?.can_load_previous === false ||
            this.hasUserScrolled
          ) {
            break;
          }
        }

        return true;
      })();

      this.historyHydrationPromises.set(conversationId, historyHydration);
      try {
        return await historyHydration;
      } finally {
        if (
          this.historyHydrationPromises.get(conversationId) === historyHydration
        ) {
          this.historyHydrationPromises.delete(conversationId);
        }
      }
    },
    async fetchPreviousMessages(scrollTop = 0) {
      const oldestMessageId = this.currentChat.messages?.[0]?.id;
      const shouldLoadMoreMessages =
        Boolean(oldestMessageId) &&
        this.currentChat.dataFetched === true &&
        // Sem esta checagem, uma conversa ja carregada por inteiro continuava
        // pedindo a pagina seguinte a cada evento de scroll -- a resposta
        // vinha vazia, mas a ida ao servidor acontecia do mesmo jeito.
        this.currentChat.allMessagesLoaded !== true &&
        !this.listLoadingStatus &&
        !this.isLoadingPrevious;

      if (
        scrollTop < PREVIOUS_MESSAGES_PREFETCH_OFFSET &&
        !this.isLoadingPrevious &&
        shouldLoadMoreMessages
      ) {
        this.isLoadingPrevious = true;
        // A ancora sai ANTES do pedido porque, quando a resposta volta, a
        // pagina nova JA esta no DOM -- medido em 2026-08-17: o patch do Vue
        // acontece antes de o `await` do dispatch retornar. Enquanto o pedido
        // viaja, cada evento de scroll re-mede esta mesma ancora (handleScroll),
        // entao continuar rolando durante a espera nao a deixa velha.
        this.captureScrollAnchor(oldestMessageId);
        try {
          const result = await this.$store.dispatch('fetchPreviousMessages', {
            conversationId: this.currentChat.id,
            before: oldestMessageId,
            limit: PREVIOUS_MESSAGES_PAGE_SIZE,
          });
          await this.$nextTick();
          this.restoreScrollAnchor();
          return Boolean(result);
        } catch (error) {
          // Ignore Error
          return false;
        } finally {
          this.isLoadingPrevious = false;
          this.scrollAnchor = null;
        }
      }

      return false;
    },

    // Aceleres 2026-08-17: guarda ONDE NA TELA esta a mensagem mais antiga ja
    // renderizada. A pagina anterior entra inteira acima dela, entao devolver
    // essa mensagem a mesma altura devolve o atendente exatamente ao trecho que
    // ele estava lendo.
    //
    // Por que nao a antiga conta de `scrollHeight - scrollTop`: aquela media
    // era tirada depois de a pagina ja estar no DOM (ver o comentario em
    // fetchPreviousMessages), entao ela ja incluia a altura recem-inserida.
    // Quando o navegador tinha compensado a insercao sozinho (scroll
    // anchoring), reescrever aquele valor DESFAZIA a compensacao: o painel
    // voltava ao mesmo `scrollTop` numerico, que na lista nova aponta uma
    // pagina inteira mais para tras -- ou seja, largava o atendente dentro do
    // bloco que acabara de carregar. E, de novo perto do topo, o prefetch
    // disparava outra vez; dai o laco de "carrega de novo".
    //
    // Medir pelo elemento resolve os dois casos com a mesma conta: se o
    // navegador ja acertou, a diferenca da zero e nada se mexe; se nao acertou,
    // corrigimos. E como a ancora volta para o lugar de origem, uma passada
    // tardia do anchoring tambem nao tem mais o que corrigir.
    captureScrollAnchor(messageId) {
      const panel = this.ensurePanel();
      const element = messageId
        ? document.getElementById(`message${messageId}`)
        : null;

      if (!panel || !element) {
        this.scrollAnchor = null;
        return;
      }

      this.scrollAnchor = {
        id: messageId,
        offset:
          element.getBoundingClientRect().top -
          panel.getBoundingClientRect().top,
      };
    },
    restoreScrollAnchor() {
      const anchor = this.scrollAnchor;
      const panel = this.ensurePanel();
      if (!panel || !anchor) return;

      const element = document.getElementById(`message${anchor.id}`);
      if (!element) return;

      const current =
        element.getBoundingClientRect().top - panel.getBoundingClientRect().top;
      const delta = current - anchor.offset;
      if (delta) {
        panel.scrollTop += delta;
      }
    },

    // Aceleres: marca que o PRÓXIMO scroll veio de gesto do usuário (roda do
    // mouse, arrasto no touch, teclas de navegação). Só com essa marca o
    // handleScroll pode mexer em hasUserScrolled — ver o porquê lá embaixo.
    // A janela é curta porque um gesto produz vários eventos de scroll em
    // sequência; ela cobre a inércia sem deixar a marca presa.
    markUserScrollIntent() {
      this.userScrollIntentUntil = Date.now() + 400;
    },
    handleScroll(e) {
      // Aceleres: a distância até o fim diz ONDE o painel está, não se o
      // USUÁRIO quis ir para lá — e essa diferença era o bug: quando a lista
      // encolhe sozinha (o álbum de imagens fecha a cada tick do relógio de 2s
      // em useReactiveNow e N bolhas viram uma só), o navegador gruda o
      // scrollTop no novo fim e dispara scroll. Lendo só a distância, isso
      // apagava o "estou lendo histórico", e aí o load da imagem seguinte
      // caía no onMediaLoad e puxava o usuário para a última mensagem.
      // (Não voltamos à antiga flag de uso único isProgrammaticScroll: aquela
      // ficava presa e abria a conversa no topo. A marca de gesto expira
      // sozinha em 400ms, então nenhum evento perdido a deixa travada.)
      const panel = this.conversationPanel || e.target;
      const distanceFromBottom =
        panel.scrollHeight - panel.scrollTop - panel.clientHeight;

      // A regra é ASSIMÉTRICA de propósito, e o motivo é a física do painel:
      //  - conteúdo que CRESCE (imagem que carrega) não move o scrollTop, logo
      //    não dispara scroll nenhum. Então "estou longe do fim" só pode vir de
      //    rolagem de verdade — pode marcar sem exigir gesto, e assim a roda do
      //    mouse, o arrasto da barra e o teclado ficam todos cobertos.
      //  - conteúdo que ENCOLHE gruda o scrollTop no novo fim e dispara scroll
      //    sozinho. É exatamente esse evento que apagava o estado do usuário.
      //    Por isso LIMPAR a marca exige gesto.
      // Enquanto a abertura ainda está "grudando" no fim (anchorToBottomOnOpen
      // / stick após envio), o painel está sob controle programático e o
      // reflow gera scrolls que não são do usuário. Ali exigimos gesto nos
      // DOIS sentidos — sem isso um reflow marcaria hasUserScrolled e a
      // conversa voltaria a abrir no topo, regressão que este arquivo já teve.
      const isAnchoring = Boolean(this.stickRaf || this.stickAfterSendRaf);
      const hasScrollIntent = Date.now() < this.userScrollIntentUntil;
      if (distanceFromBottom > 80) {
        if (!isAnchoring || hasScrollIntent) {
          this.hasUserScrolled = true;
        }
      } else if (hasScrollIntent) {
        this.hasUserScrolled = false;
      }
      // Enquanto a pagina anterior esta a caminho o atendente continua rolando;
      // re-medir a ancora aqui faz com que ela descreva o que ele esta lendo
      // AGORA, e nao o instante em que o pedido saiu.
      if (this.isLoadingPrevious && this.scrollAnchor) {
        this.captureScrollAnchor(this.scrollAnchor.id);
      }
      // Buscar a pagina anterior requer movimento em direcao ao topo. O scroll
      // automatico ao fim (mensagem nova, digitacao ou reflow) nao e uma ordem
      // para carregar historico; a direcao tambem cobre inercia e arrasto da
      // barra, sem depender de um evento wheel recente.
      const isScrollingTowardHistory = panel.scrollTop < this.lastScrollTop;
      if (isScrollingTowardHistory && (!isAnchoring || hasScrollIntent)) {
        this.fetchPreviousMessages(panel.scrollTop);
      }
      this.lastScrollTop = panel.scrollTop;
      this.lastDistanceFromBottom = distanceFromBottom;
      this.showJumpToBottom = distanceFromBottom > 200;
      emitter.emit(BUS_EVENTS.ON_MESSAGE_LIST_SCROLL);
    },
    // Quando o usuário rola pro topo pra carregar mensagens antigas, inicia um
    // timer único de 10s. Se nesse tempo nenhuma mensagem nova for carregada,
    // mostra o aviso pra consultar pelo celular. Não reinicia em cada scroll.
    maybeStartLoadingTimer() {
      if (this.spinnerTimer || this.loadingTimedOut) return;
      if (this.listLoadingStatus) return; // todas as mensagens já carregadas
      const before = this.currentChat?.messages?.length || 0;
      this.spinnerTimer = setTimeout(() => {
        this.spinnerTimer = null;
        const after = this.currentChat?.messages?.length || 0;
        if (after <= before && !this.listLoadingStatus) {
          this.loadingTimedOut = true;
        }
      }, 10000);
    },

    makeMessagesRead() {
      // Aceleres: abrir a conversa registra a leitura de QUEM abriu, sempre.
      // O portão que existia aqui ("só o dono do estado de leitura marca")
      // vinha de quando a leitura só sabia escrever em colunas compartilhadas
      // — `agent_last_seen_at` é uma só para a conta inteira e
      // `assignee_last_seen_at` é do responsável. Com o corte por usuário
      // (`aceleres_unread_user_cutoffs`) cada leitura fica no nome de quem
      // leu, e nenhuma delas mexe no não-lido dos outros. Quem preserva a
      // caixa Outros como visão de supervisão é o backend, pela perspectiva
      // da caixa (ConversationFinder#box_unread_perspective) — não este gate,
      // que só fazia a leitura do gestor não existir em lugar nenhum.
      const conversationId = this.currentChat?.id;
      if (
        !conversationId ||
        this.markingReadConversationId === conversationId
      ) {
        return;
      }

      this.markingReadConversationId = conversationId;
      this.$store
        .dispatch('markMessagesRead', {
          id: conversationId,
          auto: true,
          conversation: this.currentChat,
        })
        .finally(() => {
          if (this.markingReadConversationId === conversationId) {
            this.markingReadConversationId = null;
          }
        });
    },
    markCurrentConversationReadIfNeeded() {
      if (!this.currentChat?.id || this.unreadMessageCount <= 0) {
        return;
      }

      this.makeMessagesRead();
    },
    async handleMessageRetry(message) {
      if (!message) return;
      const payload = useSnakeCase(message);
      await this.$store.dispatch('sendMessageWithData', payload);
    },
    toggleReplyEditorSize() {
      this.resizableEditorWrapperRef?.toggleEditorExpand?.();
    },
    resetReplyEditorHeight() {
      this.resizableEditorWrapperRef?.resetEditorHeight?.();
    },
    closeSelectionInteractions() {
      emitter.emit(BUS_EVENTS.MESSAGE_INTERACTION_OPENED, {
        messageId: null,
        interaction: null,
      });
    },
    handleExitSelection() {
      this.closeSelectionInteractions();
      this.exitSelectionMode();
    },
    openSelectedForwardModal() {
      if (!this.hasSelection) return;
      this.closeSelectionInteractions();
      this.showForwardModal = true;
    },
  },
};
</script>

<template>
  <div
    ref="messagesViewRef"
    class="acl-messages-view acl-conversation-mobile-messages flex min-h-0 min-w-0 flex-1 flex-col justify-between m-0 overflow-hidden scrollbar-thin"
  >
    <div ref="topBannerRef">
      <Banner
        v-if="hasDuplicateInstagramInbox"
        color-scheme="alert"
        class="mx-2 mt-2 overflow-hidden rounded-lg"
        :banner-message="$t('CONVERSATION.OLD_INSTAGRAM_INBOX_REPLY_BANNER')"
      />
    </div>
    <ConversationPinnedMessage
      :key="`pinned-${currentChat.id}`"
      :pinned-messages="pinnedMessagesData"
      @unpin="handleUnpin"
      @scroll-to="scrollToPinnedMessage"
    />
    <!-- Aceleres 2026-08-17: o botao FLUTUA no topo da conversa. O wrapper tem
         altura zero (`h-0`) de proposito: assim ele nao ocupa lugar nenhum no
         empilhamento e as mensagens sobem ate o topo, passando por tras do
         botao. Antes ele morava numa faixa `py-2` propria que roubava altura da
         lista e parecia cortar a conversa em duas.

         `relative` fica aqui, e nao no container da view, para o `absolute` do
         botao se ancorar neste wrapper sem mudar a referencia de nenhum outro
         elemento posicionado da tela.

         A pastilha clara fica no proprio botao para ter uma unica definicao
         visual no computador e no telemovel, sem devolver a faixa de largura
         inteira que existia antes. -->
    <!-- Aceleres 2026-09-01: o bloco vem DEPOIS do banner de mensagem fixa
         de proposito. Como o wrapper tem altura zero, a ordem no DOM e a unica
         coisa que decide onde a pastilha flutua: antes do banner ela pousava
         por cima dele; aqui ela pousa logo abaixo, e sem banner o resultado e
         identico ao anterior. -->
    <div v-if="shouldShowPreviousMessagesButton" class="relative z-10 h-0">
      <button
        class="acl-load-previous-button absolute left-1/2 top-2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-md border border-[rgba(15,23,42,0.04)] bg-[rgba(255,255,255,0.88)] px-2 py-1.5 text-xs font-semibold shadow-[0_1px_2px_rgba(15,23,42,0.08)] transition-colors dark:bg-[rgba(31,44,52,0.92)] dark:text-[rgba(241,245,249,0.76)]"
        :class="
          isLoadingPrevious
            ? 'text-n-slate-10 cursor-wait'
            : 'text-n-slate-11 hover:text-n-slate-12 cursor-pointer'
        "
        :disabled="isLoadingPrevious"
        title="Ver conversas antigas"
        @click="loadMoreMessages()"
      >
        <svg v-if="!isLoadingPrevious" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="animate-spin"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
        {{ isLoadingPrevious ? 'Carregando...' : 'Ver conversas antigas' }}
      </button>
    </div>
    <MessageList
      ref="conversationPanelRef"
      class="acl-message-list conversation-panel relative z-0 flex min-h-0 flex-1 m-0 flex-col overflow-y-auto pb-4"
      :style="showSimulationTimeoutNotice ? { paddingBottom: '7rem' } : null"
      :current-user-id="currentUserId"
      :first-unread-id="firstUnreadMessageId"
      :is-an-email-channel="isAnEmailChannel"
      :inbox-supports-reply-to="inboxSupportsReplyTo"
      :messages="getMessages"
      @retry="handleMessageRetry"
    >
      <template #beforeAll>
        <transition name="slide-up">
          <!-- eslint-disable-next-line vue/require-toggle-inside-transition -->
          <li
            class="min-h-[4rem] flex flex-shrink-0 flex-grow-0 items-center flex-auto justify-center max-w-full mt-0 mr-0 mb-1 ml-0 relative first:mt-auto last:mb-0"
          >
            <Spinner
              v-if="shouldShowSpinner && !loadingTimedOut"
              class="text-n-brand"
            />
            <span
              v-else-if="loadingTimedOut"
              class="text-sm text-n-slate-11 text-center px-6 py-2 leading-snug"
            >
              Não foi possível carregar as mensagens anteriores aqui. Para
              visualizá-las, consulte a conversa pelo celular (WhatsApp).
            </span>
          </li>
        </transition>
      </template>
      <template #unreadBadge>
        <li
          v-show="unreadMessageCount != 0"
          class="list-none flex justify-center items-center"
        >
          <span
            class="shadow-lg rounded-full bg-n-brand text-white text-xs font-medium my-2.5 mx-auto px-2.5 py-1.5"
          >
            {{ unreadMessageLabel }}
          </span>
        </li>
      </template>
      <template #after>
        <ConversationLabelSuggestion
          v-if="shouldShowLabelSuggestions"
          :suggested-labels="labelSuggestions"
          :chat-labels="currentChat.labels"
          :conversation-id="currentChat.id"
        />
        <!-- "Digitando" ao estilo WhatsApp: uma bolha recebida, com três
             pontos, no lugar onde a mensagem vai aparecer. Entra por `v-if`
             (e não por `opacity`, como a seta do cartão da lista) de
             propósito: aqui a bolha DEVE ocupar espaço enquanto existe e
             devolvê-lo quando a pessoa para -- é o movimento que o WhatsApp
             faz e que o pedido descreve.

             Fica no slot `after` da MessageList, isto é, dentro do mesmo
             `<ul>` das mensagens e depois da última. É por isso que se alinha
             sozinha com as bolhas recebidas: as duas partem do `pl-16` do
             `<ul>` e as recebidas não têm coluna de avatar (ver `gridTemplate`
             em Message.vue).

             `aria-live="polite"` e não `role="status"` com texto: quem usa
             leitor de ecrã ouve "digitando" uma vez por transição, e não a
             cada quadro da animação. -->
        <transition name="acl-typing">
          <li
            v-if="isOtherSideTyping"
            class="acl-typing-row list-none flex justify-start pb-2"
            aria-live="polite"
          >
            <div class="acl-typing-bubble">
              <span class="acl-typing-dot" />
              <span class="acl-typing-dot" />
              <span class="acl-typing-dot" />
              <span class="sr-only">
                {{ $t('CONVERSATION.TYPING_INDICATOR_ALT') }}
              </span>
            </div>
          </li>
        </transition>
      </template>
    </MessageList>
    <!-- `acl-composer-over-gallery`: sobe acima do visor de midia so enquanto
         se responde a partir dele (regra em aceleres-overrides.css, junto do
         z-index 70 do telemovel, que um estilo inline nao consegue vencer). -->
    <div
      class="acl-conversation-composer-shell acl-conversation-mobile-composer relative flex flex-none flex-col bg-transparent"
      :class="{ 'acl-composer-over-gallery': galleryReplyOpen }"
      :style="{ zIndex: galleryReplyOpen ? 2147482000 : 70, isolation: 'isolate' }"
    >
      <!-- Pedido 02/09/2026: esta pílula mostrava "digitando" (texto + o
           typing.gif) e o aviso do treino simulado. O "digitando" saiu daqui e
           virou a bolha de três pontos no fim da lista; ficou só o aviso, que
           não é presença e não teria onde morar numa bolha do cliente. -->
      <div
        v-if="showSimulationTimeoutNotice"
        class="pointer-events-none absolute inset-x-0 flex justify-center"
        :style="{ top: '-3.5rem', zIndex: 2147483000 }"
      >
        <div
          class="pointer-events-auto mx-auto flex max-w-[calc(100%-2rem)] items-center rounded-full border border-n-weak bg-white py-2 pl-5 pr-4 text-xs font-semibold text-n-slate-11 shadow-md dark:bg-n-solid-3"
          :style="{ zIndex: 2147483000 }"
        >
          <span>{{ simulationTimeoutMessage }}</span>
        </div>
      </div>
      <transition name="slide-up">
        <button
          v-show="shouldDisplayJumpToBottom"
          type="button"
          class="acl-jump-to-bottom absolute top-[-69px] !size-12 max-md:!top-[-44px] max-md:!size-8 ltr:right-3 rtl:left-3 z-20 flex items-center justify-center rounded-full border border-slate-200 bg-white text-n-slate-12 shadow-[0_2px_10px_rgba(15,23,42,0.18)]"
          title="Ir para a última mensagem"
          aria-label="Ir para a última mensagem"
          @click="jumpToBottom()"
        >
          <!-- Aceleres 2026-08-18: dois desenhos no MESMO svg, e nao dois svg.
               O desktop fica com o chevron simples exatamente onde estava (a
               nota grande acima explica por que as medidas dele nao se tocam);
               o telemovel mostra o chevron duplo, pedido em 18/08. A troca e
               feita por `display` nas duas partes, dentro da media query — sem
               segundo elemento e sem repetir o svg inteiro. -->
          <svg
            class="acl-jump-icon size-[30px]"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline class="acl-jump-chevron-single" points="6 9 12 15 18 9" />
            <g class="acl-jump-chevron-double">
              <path d="m7 6 5 5 5-5" />
              <path d="m7 13 5 5 5-5" />
            </g>
          </svg>
        </button>
      </transition>
      <div
        v-if="groupDeliveryStuckTime && !isSelectionMode"
        class="mx-4 mb-2 flex items-start gap-2 rounded-lg border border-n-amber-5 bg-n-amber-2 px-3 py-2 text-sm text-n-amber-12"
        role="alert"
        data-testid="group-delivery-stuck-banner"
      >
        <span class="i-lucide-triangle-alert mt-0.5 size-4 shrink-0 text-n-amber-10" aria-hidden="true" />
        <span>{{ $t('CONVERSATION.GROUP_DELIVERY_STUCK', { time: groupDeliveryStuckTime }) }}</span>
      </div>
      <ResizableEditorWrapper
        v-show="!isCompletedTrainingConversation"
        :class="{ hidden: isSelectionMode }"
        ref="resizableEditorWrapperRef"
        :container-height="Math.max(0, containerHeight - topBannerHeight)"
      >
        <ReplyBox
          @toggle-editor-size="toggleReplyEditorSize"
          @attachment-processing-change="handleAttachmentProcessingChange"
        />
      </ResizableEditorWrapper>
      <div
        v-if="isCompletedTrainingConversation && !isSelectionMode"
        class="mx-4 mb-3 rounded-lg border border-n-weak bg-n-solid-2 px-4 py-3 text-center text-sm font-medium text-n-slate-11"
      >
        Treino concluído. Novas mensagens ficam bloqueadas nesta conversa.
      </div>

      <!-- Barra de seleção compartilhada entre celular e computador. -->
      <transition name="slide-up">
        <div
          v-if="isSelectionMode"
          class="acl-message-selection-footer mx-3 mb-[calc(0.75rem+env(safe-area-inset-bottom,0px))] grid min-h-14 min-w-0 grid-cols-[3rem_minmax(0,1fr)_3rem] items-center rounded-2xl border border-n-weak bg-white/80 px-2 py-1 shadow-[0_4px_18px_rgba(15,23,42,0.16)] backdrop-blur-xl dark:bg-n-solid-2/80 md:mx-0 md:mb-0 md:flex md:min-h-16 md:rounded-none md:border-x-0 md:border-b-0 md:bg-n-background md:px-4 md:py-2 md:backdrop-blur-none"
          role="toolbar"
          :aria-label="selectionLabels.toolbar"
        >
          <button
            type="button"
            class="order-3 flex size-12 items-center justify-center rounded-full transition-colors hover:bg-n-alpha-2 md:order-1"
            :aria-label="selectionExitLabel"
            @click="handleExitSelection"
          >
            <span class="i-lucide-x size-6 text-n-slate-11" />
          </button>
          <span
            class="order-2 min-w-0 truncate px-2 text-center text-base font-medium text-n-slate-12 md:mr-auto md:text-left"
            aria-live="polite"
          >
            {{
              $t('CONVERSATION.FORWARD.SELECTED_COUNT', {
                count: selectedCount,
              })
            }}
          </span>
          <button
            type="button"
            class="order-1 flex size-12 items-center justify-center rounded-full text-n-slate-12 transition-colors hover:bg-n-alpha-2 disabled:opacity-40 md:order-3"
            :disabled="!hasSelection"
            :aria-label="selectionForwardLabel"
            @click="openSelectedForwardModal"
          >
            <svg
              class="block"
              viewBox="0 0 24 24"
              width="28"
              height="28"
              preserveAspectRatio="xMidYMid meet"
              fill="none"
              aria-hidden="true"
            >
              <path
                fill="currentColor"
                d="M19.18 11 15.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l3.9-3.9Zm-6 1H7c-.83 0-1.54.3-2.13.88A2.9 2.9 0 0 0 4 15v3c0 .28-.1.52-.29.71A.94.94 0 0 1 3 19a.97.97 0 0 1-.71-.29A.97.97 0 0 1 2 18v-3c0-1.38.49-2.56 1.46-3.54A4.82 4.82 0 0 1 7 10h6.18L10.3 7.13a.97.97 0 0 1-.3-.72c0-.27.1-.51.3-.71.2-.18.44-.27.71-.27.28 0 .5.09.69.27l4.6 4.6c.1.1.17.2.21.32a1.2 1.2 0 0 1 0 .76.88.88 0 0 1-.21.32l-4.6 4.6a.9.9 0 0 1-.7.29c-.27-.01-.5-.1-.7-.29a1 1 0 0 1-.31-.7.87.87 0 0 1 .29-.7l2.9-2.9Z"
              />
            </svg>
          </button>
        </div>
      </transition>
    </div>
  </div>

  <!-- Forward modal -->
  <ForwardMessageModal
    v-if="showForwardModal"
    v-model:show="showForwardModal"
    :conversation-id="currentChat.id"
    :selected-messages="selectedMessages"
    @success="handleExitSelection"
  />
</template>

<style scoped>
.acl-conversation-composer-shell {
  --acl-conversation-action-size: 55px;
}

/* Bolha de "digitando" (pedido 02/09/2026). O desenho é o da bolha recebida:
   branco, 12px de raio, canto superior esquerdo reto e o mesmo bico.

   As medidas do bico são copiadas de `.left-bubble::after`
   (public/aceleres-overrides.css) -- 18x10, encostado em `left: -9px`, com o
   mesmo `clip-path`. Copiadas, e não herdadas: pôr `left-bubble` nesta bolha
   traria consigo as ~46 regras que a folha tem para essa classe, escritas para
   uma bolha com conteúdo, meta e menu de contexto. Se o bico das mensagens
   mudar, este tem de acompanhar -- é o preço de não arrastar as outras 45.

   A sombra é a mesma `drop-shadow` dupla que Message.vue aplica às bolhas:
   `filter` e não `box-shadow`, porque só ela segue o recorte do bico.

   Raio, espaçamentos e pontos em `rem`, o bico em `px`. Não é descuido: a raiz
   deste painel tem 20px, então o `rounded-xl` das bolhas (0.75rem) mede 15px,
   e escrever 12px aqui deixava esta bolha com um canto visivelmente mais fechado
   que as vizinhas. O bico, esse, está em px na folha de overrides — copiá-lo em
   rem é que o faria destoar. */
.acl-typing-bubble {
  position: relative;
  display: inline-flex;
  align-items: center;
  /* Os mesmos 12px que `.message-bubble-container .left-bubble` dá às bolhas
     recebidas, e pela mesma razão: o bico sai 9px para fora da bolha e sem
     esta margem passaria a borda esquerda da coluna. Sem ela a bolha de
     "digitando" também ficava 12px à esquerda das mensagens — medido. */
  margin-left: 12px;
  gap: 0.25rem;
  padding: 0.6rem 0.7rem;
  border-radius: 0.75rem;
  border-top-left-radius: 0;
  background: #fff;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.08))
    drop-shadow(0 4px 12px rgba(0, 0, 0, 0.08));
}

.acl-typing-bubble::after {
  content: '';
  position: absolute;
  top: 0;
  left: -9px;
  width: 18px;
  height: 10px;
  background: #fff;
  clip-path: polygon(100% 0, 50% 100%, 0 12%, 0 0);
  pointer-events: none;
}

/* No telemóvel as bolhas recebidas fecham para 9px
   (`.acl-whatsapp-mobile-scope .left-bubble`, na folha de overrides). Quem põe
   essa classe é o ConversationBox, ancestral desta lista -- por isso a regra
   segue a MESMA condição das vizinhas, e não uma media query própria que
   pudesse divergir dela. */
.acl-whatsapp-mobile-scope .acl-typing-bubble {
  border-radius: 9px;
  border-top-left-radius: 0;
}

/* Os três pontos são elementos, nunca texto: como reticências numa string, a
   animação mudaria a largura da bolha a cada quadro. Mesma razão (e mesma
   cadência) das reticências do cabeçalho, em ConversationHeader.vue. */
.acl-typing-dot {
  width: 0.375rem;
  height: 0.375rem;
  border-radius: 50%;
  background: #8696a0;
  opacity: 0.35;
  animation: acl-typing-dot 1.2s infinite ease-in-out;
}

.acl-typing-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.acl-typing-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes acl-typing-dot {
  0%,
  60%,
  100% {
    opacity: 0.35;
    transform: translateY(0);
  }
  30% {
    opacity: 1;
    transform: translateY(-2px);
  }
}

/* Aparecer e desaparecer só em opacidade: a altura entra e sai de uma vez,
   como no WhatsApp, e não há reflow animado a competir com o auto-scroll. */
.acl-typing-enter-active,
.acl-typing-leave-active {
  transition: opacity 0.15s ease;
}

.acl-typing-enter-from,
.acl-typing-leave-to {
  opacity: 0;
}

/* Aceleres 2026-08-27: o botao fica a 12px da borda em todas as larguras.
   Como o botao de audio tambem termina a 12px, os centros dos dois discos de
   48px ficam alinhados no computador. */
/* Aceleres 2026-08-18 -- este botao NAO reage ao rato mudando de forma.
   Tinha `transition-transform hover:scale-105` na lista de classes: ao passar
   por cima crescia 5% e, como a escala parte do centro, as bordas saiam do
   lugar — parecia que o botao se mexia. Foi retirado a pedido; fica com o
   mesmo tamanho e a mesma posicao sempre.
   O `transition-transform` saiu junto porque so existia para suavizar essa
   escala: a `<transition name="slide-up">` que o envolve nao tem classes
   definidas em lado nenhum, portanto nao anima nada e nao depende dele.
   Se um dia se quiser devolver alguma resposta ao rato, que seja cor ou fundo
   — nada que mexa em `transform`, `width`, `height` ou nas medidas de posicao. */
.acl-jump-to-bottom {
  color: rgba(0, 0, 0, 0.6);
}

.acl-jump-chevron-double {
  display: none;
}

@media (max-width: 767px) {
  .acl-conversation-composer-shell {
    --acl-conversation-action-size: 32px;
  }

  /* Aceleres 2026-08-25: no computador, o botão de ir para a última mensagem
     usa 48px como o botão de áudio. No telemóvel, preserva os 32px e a posição
     vertical anteriores. As medidas ficam no template sem reutilizar a
     variável dos outros controles. */

  .acl-jump-to-bottom .acl-jump-icon {
    width: 20px;
    height: 20px;
  }

  .acl-jump-to-bottom .acl-jump-chevron-single {
    display: none;
  }

  .acl-jump-to-bottom .acl-jump-chevron-double {
    display: inline;
  }
}
</style>
